import type { ExamType, ExamWorksheetData } from "./epocus-exam-worksheets";

type IndicationOf<T extends ExamType> = Exclude<
  Extract<ExamWorksheetData, { examType: T }>["indication"],
  null
>;

export const EXAM_TYPE_LABELS: Record<ExamType, string> = {
  efast: "EFAST",
  cardiac: "Cardiac",
  lung: "Lung",
  bladder: "Bladder",
  renal: "Renal",
  aorta: "Aorta",
  pregnancy: "Pregnancy",
};

// Indications are scoped per exam type, not global — the same value can appear
// under several exam types (hypotension under efast, cardiac and lung), so a
// flat value->label map would be ambiguous the moment two exam types disagreed
// on wording.
const INDICATION_LABELS: {
  [T in ExamType]: Record<IndicationOf<T>, string>;
} = {
  efast: {
    blunt_trauma: "Blunt Trauma",
    penetrating_trauma: "Penetrating Trauma",
    hypotension: "Hypotension",
  },
  cardiac: {
    dyspnea: "Dyspnea",
    hypotension: "Hypotension",
    chest_pain: "Chest Pain",
    arrest: "Cardiac Arrest",
  },
  lung: {
    dyspnea: "Dyspnea",
    hypotension: "Hypotension",
    trauma: "Trauma",
  },
  bladder: {
    urinary_retention: "Urinary Retention",
    abdominal_pain: "Abdominal Pain",
    post_void_residual: "Post-void Residual",
  },
  renal: {
    flank_pain: "Flank Pain",
    hematuria: "Hematuria",
    aki: "AKI",
  },
  aorta: {
    abdominal_pain: "Abdominal Pain",
    pulsatile_mass: "Pulsatile Mass",
    syncope: "Syncope",
    aaa_screening: "AAA Screening",
  },
  pregnancy: {
    dating: "Dating",
    vaginal_bleeding: "Vaginal Bleeding",
    pelvic_pain: "Pelvic Pain",
    suspected_ectopic: "Suspected Ectopic",
    hyperemesis: "Hyperemesis",
  },
};

export const indicationOptions = <T extends ExamType>(
  examType: T,
): ReadonlyArray<{ value: IndicationOf<T>; label: string }> =>
  Object.entries(INDICATION_LABELS[examType]).map(([value, label]) => ({
    value: value as IndicationOf<T>,
    label: label as string,
  }));

// Worksheets reach the patient history as untyped JSON off the sync API, so both
// lookups fall back to the raw value rather than rendering a blank.
export const examTypeLabel = (examType: string): string =>
  EXAM_TYPE_LABELS[examType as ExamType] ?? examType;

export const indicationLabel = (
  examType: string,
  indication: string,
): string => {
  const labels: Record<string, string> | undefined =
    INDICATION_LABELS[examType as ExamType];

  return labels?.[indication] ?? indication;
};
